import { Options } from "@contentful/rich-text-react-renderer";
import { BLOCKS } from "@contentful/rich-text-types";
import { FragmentOf, ResultOf } from "gql.tada";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { GetDefaultFooter } from "@queries/GetDefaultFooter";
import { Button } from "@components/shared/core/Button";
import { RichText } from "@components/shared/core/RichText";
import { TextInput } from "@components/shared/core/TextInput";
import { sendApiRequest } from "@services/sendApiRequest";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as y from "yup";

type FooterType = NonNullable<ResultOf<typeof GetDefaultFooter>["footerCollection"]>["items"][number];

const EmailSubmissionValidator = y.object({
  email: y.string().email().required(),
});

type NewsletterFormModel = y.InferType<typeof EmailSubmissionValidator>;

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p className="text-label text-balance">{children}</p>
    ),
  },
};

export const SimpleNewsletterSignupForm = ({ footer }: { footer: FooterType }) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, isSubmitted, isSubmitSuccessful, errors },
  } = useForm<NewsletterFormModel>({
    resolver: yupResolver(EmailSubmissionValidator),
  });

  const onSubmit = async ({ email }: NewsletterFormModel) => {
    try {
      await sendApiRequest('/signup', { email });
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const isInvalid = isSubmitted && !isSubmitSuccessful && !!errors.email;

  return (
    <form className="pb-16 dsk:pb-0 dsk:max-w-60" onSubmit={handleSubmit(onSubmit)}>
      <p className="text-meta pb-3">{footer?.newsletterFormLabel}</p>
      
      <TextInput
        className="w-full mb-1 !text-button-small"
        inputProps={{
          className: "!h-11",
          ...register("email"),
        }}
        label={footer?.newsletterEmailFieldLabel ?? ""}
        isInvalid={isInvalid}
      />

      {isInvalid && (
        <p className="text-brand-cream text-button-small mb-2">
          *{footer?.validEmailNote}
        </p>
      )}

      <Button
        variant="light-on-blur"
        size="small"
        className="w-full"
        type="submit"
        loading={isSubmitting}
        disabled={isSubmitSuccessful}
      >
        {isSubmitSuccessful ? footer?.submissionApproved : footer?.newsletterCta}
      </Button>

      <div className="pt-3">
        <RichText
          content={footer?.newsletterSignUpInfoRichText as FragmentOf<typeof RichEntryFragment> | null | undefined}
          options={options}
        />
      </div>
    </form>
  );
};